// Times of day and draft clocks, as a form offers them: one copy for the
// quiet hours, the overnight pause and every pick clock a league can choose.

/** Every half hour of a day, "00:00" to "23:30", said "12:00 AM" to "11:30 PM". */
export const HALF_HOURS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  const value = `${String(h).padStart(2, '0')}:${m}`;
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return { value, label: `${hour12}:${m} ${h < 12 ? 'AM' : 'PM'}` };
});

/**
 * The clocks the big sites offer a live draft: seconds for an evening, and
 * hours for a slow draft that fits around life (DRAFT_MODES.md §3.1: 2, 4, 8,
 * 12 and 24 hours across the platforms, ESPN's 24 since January 2025).
 */
const SECONDS = [30, 45, 60, 90, 120, 180, 300] as const;
const HOURS = [1, 2, 4, 8, 12, 24] as const;

/** A clock in words: "90 sec", "3 min", "8 hours". */
export function pickClockLabel(seconds: number): string {
  if (seconds < 120) return `${String(seconds)} sec`;
  if (seconds < 3600) return `${String(Math.round(seconds / 60))} min`;
  const hours = seconds / 3600;
  const shown = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
  return `${shown} ${hours === 1 ? 'hour' : 'hours'}`;
}

/** A slow draft: a clock of an hour or more a pick (NOTIFICATIONS.md's `draft.slow_turn`). */
export function isSlowClock(seconds: number): boolean {
  return seconds >= 3600;
}

/**
 * An auction's bid clocks (AUCTION.md §4): Yahoo's 20 seconds and its
 * neighbors for an evening, and hours for a slow auction, which puts several
 * players up at once.
 */
export function bidClockOptions(): { value: string; label: string }[] {
  return [10, 15, 20, 30, 45, 60, ...HOURS.map((h) => h * 3600)].map((s) => ({
    value: String(s),
    label: pickClockLabel(s),
  }));
}

/** An auction's nomination clocks: seconds live, hours in a slow auction. */
export function nominationClockOptions(slow: boolean): { value: string; label: string }[] {
  const all = slow ? HOURS.map((h) => h * 3600) : [15, 30, 45, 60, 90, 120];
  return all.map((s) => ({ value: String(s), label: pickClockLabel(s) }));
}

/** Every clock to choose from, and `current` too when a league has one of its own. */
export function pickClockOptions(current?: number): { value: string; label: string }[] {
  const all: number[] = [...SECONDS, ...HOURS.map((h) => h * 3600)];
  if (current !== undefined && !all.includes(current)) all.push(current);
  return all.sort((a, b) => a - b).map((s) => ({ value: String(s), label: pickClockLabel(s) }));
}
